import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Box, Typography, CircularProgress, Card, CardContent } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (!isLoading && !user) {
      // Start countdown when user is not authenticated
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [user, isLoading, navigate]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'grey.50'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Checking authentication...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Show access denied message if user is not authenticated
  if (!user) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2
        }}
      >
        <Card 
          sx={{ 
            maxWidth: 500, 
            width: '100%', 
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: 3
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
              }}
            >
              <LockIcon 
                sx={{ 
                  fontSize: 48, 
                  color: 'error.main',
                  mr: 2
                }} 
              />
              <Typography 
                variant="h4" 
                fontWeight="bold" 
                color="error.main"
              >
                Access Denied
              </Typography>
            </Box>
            
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ mb: 2 }}
            >
              You are not allowed to view this page
            </Typography>
            
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ mb: 3 }}
            >
              Please log in to access your dashboard
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 2,
                mb: 3
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Redirecting to home page in {countdown} seconds...
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              If you are not redirected automatically, please click{' '}
              <Typography 
                component="span" 
                color="primary.main" 
                sx={{ 
                  textDecoration: 'underline', 
                  cursor: 'pointer' 
                }}
                onClick={() => navigate('/')}
              >
                here
              </Typography>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
